import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type {
  UseCreditPreflightCheckArgs,
  UseCreditPreflightCheckResult,
} from "./types";

export const useCreditPreflightCheck = ({
  pdfFileCount,
  isGlobalAdmin,
  uploadKind,
}: UseCreditPreflightCheckArgs): UseCreditPreflightCheckResult => {
  const { t } = useTranslation();
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const runCheck = useCallback(async (): Promise<boolean> => {
    setError(null);

    if (isGlobalAdmin) {
      return true;
    }

    setIsChecking(true);
    try {
      const balance = await api.getCreditBalance();

      if (balance.available <= 0) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CREDITS_BLOCKED, {
          uploadKind,
          reason: "noCredits",
          pdfFileCount,
        });
        setError(
          t(
            "smartSurveyor.credits.noCreditsRemaining",
            "Your organization has no credits remaining. Contact your administrator.",
          ),
        );
        return false;
      }

      if (pdfFileCount > 0 && balance.available < pdfFileCount) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CREDITS_BLOCKED, {
          uploadKind,
          reason: "notEnoughCredits",
          pdfFileCount,
        });
        setError(
          t(
            "smartSurveyor.credits.notEnoughCredits",
            "Not enough credits. Your organization has {{available}} credits but you are uploading {{pdfCount}} PDF files.",
            { available: balance.available, pdfCount: pdfFileCount },
          ),
        );
        return false;
      }

      return true;
    } catch (checkError) {
      console.error("Failed to check credit balance:", checkError);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CREDITS_BLOCKED, {
        uploadKind,
        reason: "checkFailed",
        pdfFileCount,
      });
      setError(
        t(
          "smartSurveyor.credits.checkFailed",
          "Failed to verify credits. Please try again.",
        ),
      );
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [isGlobalAdmin, pdfFileCount, uploadKind, t]);

  return { isChecking, error, runCheck, clearError };
};
